import { useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { useEffect, useState } from "react";
import { JartilandLogo } from "../icons/JartilandLogo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { TransitionLink } from "@/providers/TransitionProvider";
import { motion } from "framer-motion";

const navLinks = [
  { to: "/", label: "Inicio" },
  { to: "/periodico", label: "Periódico" },
  { to: "/hermandades", label: "Hermandades" },
  { to: "/bandas", label: "Bandas" },
  { to: "/social", label: "Social" },
  { to: "/gallery", label: "Galería" },
  { to: "/mod-updates", label: "Mod Cofrade" },
  { to: "/noticias", label: "Noticias" },
  { to: "/apply", label: "Formulario de Acceso" },
  { to: "/jartinet/home", label: "Jartinet" },
];

export function Header() {
  const location = useLocation();
  const pathname = location.pathname;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const NavLink = ({ to, label }: { to: string; label: string }) => {
    const isActive = pathname === to;
    return (
      <TransitionLink
        to={to}
        className={cn(
          "relative px-1 py-2 text-sm font-medium transition-colors hover:text-primary",
          isActive ? "text-primary" : "text-foreground/70"
        )}
        onClick={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
      >
        {label}
        {isActive && (
          <motion.span
            layoutId="nav-underline"
            className="absolute -bottom-[3px] left-0 right-0 h-[2px] rounded-full bg-primary shadow-[0_0_8px_rgba(250,204,21,0.7)]"
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
          />
        )}
      </TransitionLink>
    );
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-300",
        isScrolled
          ? "border-b border-white/10 bg-background/80 backdrop-blur-xl shadow-[0_4px_30px_rgba(0,0,0,0.3)]"
          : "border-b border-transparent bg-gradient-to-b from-background/60 to-transparent backdrop-blur-sm"
      )}
    >
      <div className="container flex h-16 items-center">
        <div className="mr-4 flex items-center">
          <TransitionLink to="/" className="group flex items-center gap-2.5">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-primary/40 blur-md transition-opacity duration-300 group-hover:opacity-80" />
              <JartilandLogo className="relative ring-1 ring-primary/30 transition-transform duration-300 group-hover:scale-105" />
            </div>
            <span className="font-headline text-lg font-bold tracking-tight">
              <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">
                Jartiland
              </span>
            </span>
          </TransitionLink>
        </div>
        <nav className="hidden items-center space-x-7 text-sm font-medium md:flex">
          {navLinks.map((link) => (
            <NavLink key={link.to} {...link} />
          ))}
        </nav>
        <div className="flex flex-1 items-center justify-end space-x-4">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" className="md:hidden">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Abrir menú</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="border-white/10 bg-background/95 backdrop-blur-xl">
              <div className="flex flex-col gap-4 py-6">
                <TransitionLink to="/" className="mb-4 flex items-center gap-2" onClick={() => setIsMobileMenuOpen(false)}>
                  <JartilandLogo className="ring-1 ring-primary/30" />
                  <span className="font-headline text-lg font-bold tracking-tight">
                    <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">
                      Jartiland
                    </span>
                  </span>
                </TransitionLink>
                {navLinks.map((link) => (
                  <NavLink key={link.to} {...link} />
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
